import React, { useState } from 'react';
import { Input, InputNumber, Tag, Alert } from 'antd';
import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { useDispatch } from 'react-redux';

import { DevPlanAdmin, patchPlanMeta } from 'state';
import { usePatchBuffer } from '../hooks';
import type { Readiness } from '../hooks';
import { Field, Section, LockedRow } from './inspectorBits';
import RecommendationsPanel from './RecommendationsPanel';

type Props = { plan: DevPlanAdmin; editable: boolean; readiness: Readiness };

const PlanOverview: React.FC<Props> = ({ plan, editable, readiness }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState(plan.name);
  const [description, setDescription] = useState(plan.description);
  const { push } = usePatchBuffer<{ name: string; description: string }>((p) =>
    dispatch(patchPlanMeta({ code: plan.code, ...p }))
  );

  return (
    <div className="devadmin-insp">
      <div className="devadmin-insp__head">
        <div>
          <div className="devadmin-insp__kind">Plan</div>
          <div className="devadmin-insp__title">{name}</div>
        </div>
        <Tag color={plan.status === 'draft' ? 'gold' : 'green'}>{plan.status}</Tag>
      </div>

      {!editable && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Published — read-only"
          description="Clone this tree to make changes."
        />
      )}

      <Section title="Metadata">
        <Field
          label="Name"
          info="The tree's title on the trainee's plan-picker card and the plan header. Display only — no effect on structure or scoring."
        >
          <Input
            value={name}
            disabled={!editable}
            onChange={(e) => {
              setName(e.target.value);
              push({ name: e.target.value });
            }}
          />
        </Field>
        <Field
          label="Description"
          info="Blurb on the trainee's plan-picker card. Display only."
        >
          <Input.TextArea
            value={description}
            disabled={!editable}
            autoSize={{ minRows: 2, maxRows: 6 }}
            onChange={(e) => {
              setDescription(e.target.value);
              push({ description: e.target.value });
            }}
          />
        </Field>
        <Field
          label="Order"
          info="A tiebreaker for the order trees appear in the picker — applied after family and version, so it rarely changes anything on its own."
        >
          <InputNumber
            value={plan.order}
            disabled={!editable}
            onChange={(v) =>
              v != null && dispatch(patchPlanMeta({ code: plan.code, order: v }))
            }
          />
        </Field>
        <LockedRow
          label="Plan code"
          value={plan.code}
          why="The tree's permanent slug — its URL and the key each trainee's saved progress is stored under. Fixed after creation; changing it would orphan every trainee's progress and break the link."
        />
      </Section>

      <Section title="Publish readiness" hint="server confirms on publish">
        {readiness.ready ? (
          <div className="devadmin-ready ok">
            <CheckCircleFilled /> Ready to publish — no blockers found.
          </div>
        ) : (
          <div className="devadmin-ready warn">
            <div className="devadmin-ready__head">
              <WarningFilled /> {readiness.problems.length} blocker
              {readiness.problems.length === 1 ? '' : 's'}
            </div>
            <ul className="devadmin-ready__list">
              {readiness.problems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* Recommendations. Only meaningful on a PUBLISHED tree — a draft is
          invisible to trainees, so a recommendation on it could never be seen. */}
      {plan.status === 'published' && (
        <RecommendationsPanel planCode={plan.code} />
      )}
    </div>
  );
};

export default PlanOverview;
